// src/hooks/useKnowledgeDB.js
import { useAuth } from "./useAuth";

export default function useKnowledgeDB() {
  const { api } = useAuth();

  /**
   * 새로운 지식 데이터베이스 생성
   * @param {Object} data - 생성 데이터
   * @param {string} data.database_name - DB 이름
   * @param {string} data.database_description - DB 설명
   * @param {string} data.file_name - 파일명
   * @param {string} data.file_type - 파일 타입 (pdf)
   * @param {string} data.b64 - base64 인코딩된 파일 데이터
   */
  const createDatabase = async (data) => {
    try {
      console.log(data);
      const response = await api.post(
        "/knowledge_db/create_database_20250822",
        data
      );

      // status는 true이지만 invalid_format이 있는 경우
      if (response.data.status && response.data.invaild_format) {
        return {
          ok: false,
          error: "PDF 파일만 업로드 가능합니다.",
          invalidFormat: true,
        };
      }

      if (!response.data.status) {
        return { ok: false, error: "데이터베이스 생성에 실패했습니다." };
      }

      return { ok: true };
    } catch (error) {
      console.error("Create database error:", error);
      return {
        ok: false,
        error:
          error.response?.data?.message ||
          "데이터베이스 생성 중 오류가 발생했습니다.",
      };
    }
  };

  /**
   * 데이터베이스 목록 조회
   */
  const getDatabases = async () => {
    try {
      const response = await api.get("/knowledge_db/get_database_20250826");

      // status가 false인 경우만 에러
      if (!response.data.status) {
        return {
          ok: false,
          databases: [],
          error: "데이터베이스 목록을 가져올 수 없습니다.",
        };
      }

      const databases = response.data.data || [];

      // 데이터 정제 및 변환
      const formattedDatabases = databases.map((db) => ({
        ...db,
        // 타입 라벨 추가
        typeLabel: db.base_type === "build_typ_s001" ? "공유받음" : "직접구축",
        // 상태 라벨 추가
        statusLabel: getStatusLabel(db.database_status),
        // 공유 가능 여부 - 직접 구축한 것이면서 complete 상태이고 deleted가 false인 경우만
        canShare:
          db.base_type === "build_typ_d001" &&
          db.database_status === "complete" &&
          !db.deleted,
        // 진행률 계산
        progress:
          db.max_steps > 0
            ? Math.round((db.cur_steps / db.max_steps) * 100)
            : 0,
      }));

      // deleted가 false인 것만 필터링
      const activeDatabases = formattedDatabases.filter((db) => !db.deleted);

      return { ok: true, databases: activeDatabases };
    } catch (error) {
      console.error("Get databases error:", error);
      return {
        ok: false,
        databases: [],
        error:
          error.response?.data?.message ||
          "데이터베이스 목록 조회 중 오류가 발생했습니다.",
      };
    }
  };

  /**
   * 데이터베이스 공유
   * @param {string} databaseId - 공유할 DB ID
   * @param {string[]} shareUserIds - 공유받을 사용자 ID 배열
   */
  const shareDatabase = async (databaseId, shareUserIds) => {
    try {
      const response = await api.post("/knowledge_db/share_database_20250826", {
        database_id: databaseId,
        share_user_ids: shareUserIds,
      });

      // status는 true이지만 특별한 플래그가 있는 경우들
      if (response.data.status) {
        if (response.data.unknwon) {
          return {
            ok: false,
            error: "데이터베이스를 찾을 수 없습니다.",
            unknown: true,
          };
        }
        if (response.data.unable) {
          return {
            ok: false,
            error:
              "이 데이터베이스는 공유할 수 없습니다. 완료 상태의 직접 구축한 DB만 공유 가능합니다.",
            unable: true,
          };
        }
        // 모든 체크를 통과한 경우에만 성공
        return { ok: true, message: "성공적으로 공유되었습니다." };
      }

      // status가 false인 경우
      return { ok: false, error: "데이터베이스 공유에 실패했습니다." };
    } catch (error) {
      console.error("Share database error:", error);
      return {
        ok: false,
        error:
          error.response?.data?.message ||
          "데이터베이스 공유 중 오류가 발생했습니다.",
      };
    }
  };

  /**
   * 데이터베이스 삭제
   * @param {string} databaseId - 삭제할 DB ID
   */
  const deleteDatabase = async (databaseId) => {
    try {
      const response = await api.post("/knowledge_db/delete_dabase_20250826", {
        database_id: databaseId,
      });

      // status는 true이지만 특별한 플래그가 있는 경우
      if (response.data.status) {
        if (response.data.unknwon) {
          return {
            ok: false,
            error: "데이터베이스를 찾을 수 없습니다.",
            unknown: true,
          };
        }
        // unknwon이 없으면 성공
        return { ok: true, message: "데이터베이스가 삭제되었습니다." };
      }

      // status가 false인 경우
      return { ok: false, error: "데이터베이스 삭제에 실패했습니다." };
    } catch (error) {
      console.error("Delete database error:", error);
      return {
        ok: false,
        error:
          error.response?.data?.message ||
          "데이터베이스 삭제 중 오류가 발생했습니다.",
      };
    }
  };

  /**
   * 상태 라벨 반환
   */
  const getStatusLabel = (status) => {
    const statusMap = {
      analyzing: "분석중",
      progressing: "빌드중",
      complete: "완료",
      faild: "실패", // 백엔드 오타 그대로 사용
      failed: "실패",
    };
    return statusMap[status] || "알 수 없음";
  };

  /**
   * 파일을 Base64로 변환
   * @param {File} file - 변환할 파일
   */
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // data:application/pdf;base64,xxxxx 형식에서 base64 부분만 추출
        const base64 = reader.result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  /**
   * PDF 파일 검증
   * @param {File} file - 검증할 파일
   */
  const validatePDFFile = (file) => {
    if (!file) {
      return { valid: false, error: "파일을 선택해주세요." };
    }

    // MIME 타입 검증
    if (file.type !== "application/pdf") {
      return { valid: false, error: "PDF 파일만 업로드 가능합니다." };
    }

    // 파일 크기 검증 (예: 100MB)
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      return { valid: false, error: "파일 크기는 100MB 이하여야 합니다." };
    }

    // 파일 확장자 검증
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith(".pdf")) {
      return { valid: false, error: "PDF 파일만 업로드 가능합니다." };
    }

    return { valid: true };
  };

  /**
   * 파일 크기 포맷팅
   */
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "0 B";

    const units = ["B", "KB", "MB", "GB"];
    const k = 1024;
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${units[i]}`;
  };

  /**
   * 날짜 포맷팅
   */
  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    // 오늘
    if (days === 0) {
      const hours = Math.floor(diff / (1000 * 60 * 60));
      if (hours === 0) {
        const minutes = Math.floor(diff / (1000 * 60));
        return minutes === 0 ? "방금 전" : `${minutes}분 전`;
      }
      return `${hours}시간 전`;
    }

    // 어제
    if (days === 1) return "어제";

    // 일주일 이내
    if (days < 7) return `${days}일 전`;

    // 그 외
    return date.toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  /**
   * 데이터베이스 통계 계산
   */
  const calculateStats = (databases) => {
    return {
      total: databases.length,
      direct: databases.filter((db) => db.base_type === "build_typ_d001")
        .length,
      shared: databases.filter((db) => db.base_type === "build_typ_s001")
        .length,
      complete: databases.filter((db) => db.database_status === "complete")
        .length,
      progressing: databases.filter(
        (db) =>
          db.database_status === "analyzing" ||
          db.database_status === "progressing"
      ).length,
      failed: databases.filter(
        (db) =>
          db.database_status === "faild" || db.database_status === "failed"
      ).length,
      totalSize: databases.reduce((sum, db) => sum + (db.data_size || 0), 0),
      totalChunks: databases.reduce((sum, db) => sum + (db.chunk_size || 0), 0),
    };
  };

  return {
    // API 함수들
    createDatabase,
    getDatabases,
    shareDatabase,
    deleteDatabase,

    // 유틸리티 함수들
    fileToBase64,
    validatePDFFile,
    formatFileSize,
    formatDate,
    calculateStats,
    getStatusLabel,
  };
}
